import React, { useEffect, useState } from "react";
import { toast } from 'react-toastify';

import {
    saveTvApkSettings,
    getTvApkSettings
} from "../../../../../Services/TvApksService/TvApkService";
import { Form } from "react-bootstrap";

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/jpg"];

export const isAllowedLogoFile = (file) => {
    if (!file) return false;

    const normalizedType = (file.type || "").toLowerCase();
    const extension = (file.name || "").split(".").pop()?.toLowerCase();

    return ALLOWED_LOGO_TYPES.includes(normalizedType) || ["png", "jpg", "jpeg"].includes(extension || "");
};

const TvApkSettings = ({ tvApkId }) => {
    const [loading, setLoading] = useState(false);
    const [isSettingsExists, setIsSettingsExists] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [logoVersion, setLogoVersion] = useState(Date.now());
    const [formData, setFormData] = useState({
        logo: null,
        contact_email: "",
        contact_phone: "",
    });
    const [errors, setErrors] = useState({
        contact_email: "",
        contact_phone: "",
        logo: "",
    });
    const [logoPreview, setLogoPreview] = useState("");

    useEffect(() => {
        if (tvApkId) {
            setLogoPreview("");
            setFormData({
                logo: null,
                contact_email: "",
                contact_phone: "",
            });
            fetchSettings();
        }
    }, [tvApkId, refreshKey]);

    const fetchSettings = async () => {
        try {
            setLoading(true);

            const response = await getTvApkSettings(tvApkId);

            // Settings exist only when API returns an actual settings object
            if (response?.data) {
                setIsSettingsExists(true);

                setLogoPreview(
                    response.data.logo_url
                        ? `${response.data.logo_url}&v=${logoVersion}`
                        : ""
                );
                setFormData({
                    logo: null,
                    contact_email: response.data.contact_email || "",
                    contact_phone: response.data.contact_phone || "",
                });
            } else {
                setIsSettingsExists(false);
                setLogoPreview("");

                setFormData({
                    logo: null,
                    contact_email: "",
                    contact_phone: "",
                });
            }

        } catch (error) {
            // If backend returns 404 when settings do not exist
            if (error?.response?.status === 404) {
                setIsSettingsExists(false);
                setLogoPreview("");

                setFormData({
                    logo: null,
                    contact_email: "",
                    contact_phone: "",
                });
            } else {
                console.error("Error fetching settings:", error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        const extension = file.name.split(".").pop().toLowerCase();

        if (!file.type.startsWith("image/")) {
            setErrors((prev) => ({
                ...prev,
                logo: "Please select a valid image file."
            }));

            e.target.value = "";

            setFormData((prev) => ({
                ...prev,
                logo: null
            }));

            setLogoPreview("");

            return;
        }

        // Reject JFIF
        if (!["png", "jpg", "jpeg"].includes(extension)) {
            setErrors((prev) => ({
                ...prev,
                logo: "Only PNG, JPG and JPEG files are allowed."
            }));

            e.target.value = "";

            setFormData((prev) => ({
                ...prev,
                logo: null
            }));

            setLogoPreview("");

            return;
        }

        setErrors((prev) => ({
            ...prev,
            logo: ""
        }));

        setFormData((prev) => ({
            ...prev,
            logo: file
        }));

        setLogoPreview(URL.createObjectURL(file));
    };

    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        setErrors((prev) => {

            const updatedErrors = {
                ...prev,
                [name]: ""
            };

            if (name === "contact_email") {

                if (!value.trim()) {

                    updatedErrors.contact_email = isSettingsExists
                        ? ""
                        : "Contact email is required";

                } else if (
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
                ) {

                    updatedErrors.contact_email =
                        "Please enter a valid email address";

                }

            }

            if (name === "contact_phone") {

                if (!value.trim() && !isSettingsExists) {

                    updatedErrors.contact_phone =
                        "Contact phone is required";

                }

            }

            return updatedErrors;

        });

    };

    const handleSave = async () => {
        const email = formData.contact_email.trim();
        const phone = formData.contact_phone.trim();

        const newErrors = {
            contact_email: "",
            contact_phone: "",
            logo: "",
        };

        // Required validation only while creating settings
        if (!isSettingsExists) {
            if (!email) {
                newErrors.contact_email = "Contact email is required";
            }

            if (!phone) {
                newErrors.contact_phone = "Contact phone is required";
            }

            if (!formData.logo) {
                newErrors.logo = "Logo is required";
            }
        }

        // Email format validation during both create and update
        if (
            email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        ) {
            newErrors.contact_email = "Please enter a valid email address";
        }

        setErrors(newErrors);

        // Stop API call if any validation error exists
        if (Object.values(newErrors).some((error) => error)) {
            return;
        }

        try {
            setLoading(true);

            const payload = new FormData();

            payload.append("tv_apk_id", tvApkId);

            if (email) {
                payload.append("contact_email", email);
            }

            if (phone) {
                payload.append("contact_phone", phone);
            }

            if (formData.logo) {
                payload.append("banner", formData.logo);
            }

            const response = await saveTvApkSettings(payload);

            toast.success(response?.message)

            setIsSettingsExists(true);
            setLogoPreview("");
            setFormData((prev) => ({ ...prev, logo: null }));

            if (formData.logo instanceof File) {
                setLogoPreview(URL.createObjectURL(formData.logo));
            }

            setLogoVersion(Date.now());
            setRefreshKey((prev) => prev + 1);
        } catch (error) {
            console.error("Error saving settings:", error);

            alert(
                error?.response?.data?.message ||
                "Failed to save settings"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="card">
                <div className="card-body">
                    <h5 className="card-title">
                        TV APK Settings
                    </h5>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label>
                                Contact Email{" "}
                                {!isSettingsExists && (
                                    <span className="text-danger">*</span>
                                )}
                            </label>

                            <Form.Control
                                type="email"
                                className="form-control"
                                name="contact_email"
                                value={formData.contact_email}
                                onChange={handleChange}
                                isInvalid={!!errors.contact_email}
                            />

                            <Form.Control.Feedback
                                type="invalid"
                                style={{
                                    display: errors.contact_email ? "block" : "none"
                                }}
                            >
                                {errors.contact_email}
                            </Form.Control.Feedback>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label>
                                Contact Phone{" "}
                                {!isSettingsExists && (
                                    <span className="text-danger">*</span>
                                )}
                            </label>

                            <Form.Control
                                type="text"
                                className="form-control"
                                name="contact_phone"
                                value={formData.contact_phone}
                                onChange={handleChange}
                                isInvalid={!!errors.contact_phone}
                            />

                            <Form.Control.Feedback
                                type="invalid"
                                style={{
                                    display: errors.contact_phone ? "block" : "none"
                                }}
                            >
                                {errors.contact_phone}
                            </Form.Control.Feedback>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label>
                                Logo{" "}
                                {!isSettingsExists && (
                                    <span className="text-danger">*</span>
                                )}
                            </label>

                            <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleLogoChange}
                                isInvalid={!!errors.logo}
                            />


                            <Form.Control.Feedback
                                type="invalid"
                                style={{
                                    display: errors.logo ? "block" : "none"
                                }}
                            >
                                {errors.logo}
                            </Form.Control.Feedback>
                            <small className="text-muted d-block mt-1">
                                Recommended logo size: <strong>69 × 69 pixels</strong>.
                            </small>
                            {logoPreview && (
                                <img
                                    src={logoPreview}
                                    alt="Logo Preview"
                                    className="mt-2 img-thumbnail"
                                    style={{ maxHeight: "120px" }}
                                />
                            )}
                        </div>
                    </div>

                    <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading
                            ? "Saving..."
                            : isSettingsExists
                                ? "Update Settings"
                                : "Create Settings"
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TvApkSettings;